// Use an immediately invoked function expression (IIFE) to create a closure
(function () {
  const swatchRequests = new Map();

  // The swap only reads product-info, new arrivals and love-this-print, so request just those sections (about a
  // sixth of the full product document to download and parse). Any section missing from the target product's
  // template falls back to the full page, which is what this used to fetch.
  function swatchSectionIds() {
    const ids = [document.querySelector('product-info')?.dataset.section];
    for (const selector of ['.section-new-arrivals', '.section-love-this-print']) {
      const id = document.querySelector(selector)?.closest('.shopify-section')?.id.replace('shopify-section-', '');
      if (id) ids.push(id);
    }
    return ids.every(Boolean) ? ids : null;
  }

  function fetchText(url) {
    return fetch(url).then((response) => {
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return response.text();
    });
  }

  function fetchSwatch(url) {
    const ids = swatchSectionIds();
    if (!ids) return fetchText(url);

    const sectionsUrl = new URL(url, window.location.origin);
    sectionsUrl.searchParams.set('sections', ids.join(','));
    return fetch(sectionsUrl)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then((sections) => (ids.every((id) => sections[id]) ? ids.map((id) => sections[id]).join('') : fetchText(url)));
  }

  function loadSwatch(url) {
    if (!swatchRequests.has(url)) {
      const request = fetchSwatch(url).catch((error) => {
        swatchRequests.delete(url);
        throw error;
      });
      swatchRequests.set(url, request);
    }
    return swatchRequests.get(url);
  }

  function prefetchSwatch(event) {
    const link = event.target.closest?.('.cusSwatchesBody .swatchesPanel-item__link, .cusSwatchesBody .color-swatch-link');
    if (!link?.href || new URL(link.href).pathname === window.location.pathname) return;
    if (event.type === 'pointerover' && link.contains(event.relatedTarget)) return;

    // Speculative failures must not prevent a later click from retrying.
    loadSwatch(link.href).catch(() => {});
  }

  // Private variable, not accessible from outside this scope
  let swatchRequestInProgress = false;
  window.productNavigationUrl = `${window.location.pathname}${window.location.search}`;

  function swatchesModalOpen(title) {
    const selectedPrintImg = document.querySelector(`.swatchesHoverUniqueImage[data-title="${title}"]`)?.getAttribute('data-src');
    if (!selectedPrintImg) {
      return;
    }
    let imgFirstSpl;
    let imgSecSpl;
    let finalImageUrl;
    const fileExtensions = ['.jpg', '.png', '.jpeg'];
    const selectedExtension = fileExtensions.find((ext) => selectedPrintImg.includes(ext));
    let aspectRatio = `_400x`;
    if (selectedExtension) {
      imgFirstSpl = selectedPrintImg.split(selectedExtension)[0];
      imgSecSpl = selectedPrintImg.split(selectedExtension)[1];

      finalImageUrl = `${imgFirstSpl}${selectedExtension}${imgSecSpl}`;
      if (finalImageUrl.includes('_400x')) {
        finalImageUrl = finalImageUrl;
      } else {
        finalImageUrl = `${imgFirstSpl}${aspectRatio}${selectedExtension}${imgSecSpl}`;
      }
    }
    document.querySelector(`.swatches-modal[data-title="${title}"] img`).src = finalImageUrl;
  }

  function updateOtherSections(doc) {
    try {
      const newArrivalsSection = document.querySelector('.section-new-arrivals');
      if (newArrivalsSection) {
        newArrivalsSection.innerHTML = doc.querySelector('.section-new-arrivals').innerHTML;
      }

      const loveThisPrintSection = document.querySelector('.section-love-this-print');
      if (loveThisPrintSection) {
        loveThisPrintSection.innerHTML = doc.querySelector('.section-love-this-print').innerHTML;
      }

      initProductListSlider();
      initColorSwatchSlider();
    } catch (error) {
      console.error('Error updating other sections:', error);
    }
  }

  async function colorSwatchPDPChange(event) {
    event.preventDefault();

    const mainProductContainer = document.querySelector('product-info');
    const link = event.currentTarget.closest('a');

    const url = link.href;
    if (new URL(url).pathname === window.location.pathname || swatchRequestInProgress || !mainProductContainer) return;
    swatchRequestInProgress = true;

    try {
      mainProductContainer.classList.add('loading');

      // Let the tap paint its loading state before parsing and swapping (a hover-prefetched response would
      // otherwise resolve in the same task as the click).
      const [colorHtml] = await Promise.all([
        loadSwatch(url),
        new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve))),
      ]);

      const parser = new DOMParser();
      const doc = parser.parseFromString(colorHtml, 'text/html');

      const newMainProduct = doc.querySelector('product-info');
      const productMedia = newMainProduct.querySelector('.product__media-wrapper');

      const quickAddModals = document.querySelectorAll('.quick-add-modal');
      quickAddModals.forEach((modal) => {
        modal.remove();
      });

      if (newMainProduct && mainProductContainer) {
        const producModal = document.getElementById(`ProductModal-${mainProductContainer.dataset.section}`);
        if (producModal) producModal.remove();

        // Get important data attributes before replacing content
        const oldUrl = mainProductContainer.dataset.url;
        const oldProductId = mainProductContainer.dataset.productId;
        const oldSection = mainProductContainer.dataset.section;

        // Change URL without reloading the page
        window.history.pushState({ path: url }, '', url);
        window.productNavigationUrl = `${window.location.pathname}${window.location.search}`;

        // Replace the content
        mainProductContainer.innerHTML = newMainProduct.innerHTML;

        // Restore important data attributes
        mainProductContainer.dataset.url = newMainProduct.dataset.url || oldUrl;
        mainProductContainer.dataset.productId = newMainProduct.dataset.productId || oldProductId;
        mainProductContainer.dataset.section = oldSection;

        // Re-initialize the nedia slider
        mainProductContainer.initializeMediaGallery();

        // Reinitialize pdpScrollAndPlay functionality after content replacement
        // Use setTimeout to ensure DOM is fully updated
        setTimeout(() => {
          if (window.initPdpScrollAndPlay) {
            window.initPdpScrollAndPlay();
          }
        }, 100);

        // Reinitialize Yotpo widgets
        if (window.yotpo) {
          window.yotpo.refreshWidgets();
        }

        updateOtherSections(doc);

        // // Trigger Easy Size readiness event
        // window.dispatchEvent(new Event('easySizeReady'));
      }
    } catch (error) {
      console.error('Error in colorSwatchPDPChange:', error);
    } finally {
      // Reset loading states
      if (mainProductContainer) {
        mainProductContainer.classList.remove('loading');
      }
      swatchRequestInProgress = false;
    }
  }

  // Expose these functions to the global scope
  window.swatchesModalOpen = swatchesModalOpen;
  window.colorSwatchPDPChange = colorSwatchPDPChange;

  document.addEventListener('pointerover', prefetchSwatch);
  document.addEventListener('focusin', prefetchSwatch);

  window.addEventListener('popstate', function () {
    const currentProductUrl = `${window.location.pathname}${window.location.search}`;
    const productUrlChanged = currentProductUrl !== window.productNavigationUrl;
    window.productNavigationUrl = currentProductUrl;

    // Hash-only changes (Rivo #rivo-favorites, in-page anchors) fire popstate
    // in Chrome but are not color-swatch back/forward. Do not reload those.
    if (!productUrlChanged) return;

    location.reload();
  });
})();
